import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Footer from "../components/Footer";
import Icon from "../components/Icon";
import { PRICING, paymentLinkFor } from "../config/pricing";
import { isPaidUnlocked } from "../utils/paidUnlock";
import { setPageMeta, breadcrumbLd } from "../utils/meta";

/**
 * The one paid thing on the site: Scene Analysis. Everything else — every
 * recipe, every reference page, the builder — stays free. This page explains
 * the $5 unlock and sends the buyer to a Stripe Payment Link (no server
 * required; Stripe hosts checkout and redirects back to /unlock).
 */
export default function Pricing() {
  const [params] = useSearchParams();
  const [unlocked, setUnlocked] = useState(false);

  // Where to send the buyer back to after paying (defaults to the analyzer
  // entry). Passed through the Payment Link so /unlock can return them.
  const returnTo = params.get("from") || "/light-meter";

  useEffect(() => {
    setUnlocked(isPaidUnlocked());
    setPageMeta({
      title: "Unlock Scene Analysis for $5",
      description:
        "Unlock on-device scene analysis: measure the real light and pan speed with your phone's sensors. A one-time $5 unlock.",
      path: "/pricing/",
      jsonLdId: "pricing-jsonld",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Product",
            name: PRICING.productName,
            description: PRICING.blurb,
            offers: {
              "@type": "Offer",
              price: "5.00",
              priceCurrency: PRICING.priceCurrency,
              availability: "https://schema.org/InStock",
            },
          },
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "Unlock Scene Analysis", path: "/pricing/" }]),
        ],
      },
    });
  }, []);

  const link = paymentLinkFor(returnTo);
  const linkReady = link && !link.includes("PLACEHOLDER");

  return (
    <main className="pricing-page">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span> <span>Unlock Scene Analysis</span>
      </nav>

      <div className="pricing-card panel">
        <div className="pricing-head">
          <span className="pricing-icon" aria-hidden="true"><Icon name="light" size={26} /></span>
          <p className="eyebrow">One-time unlock</p>
          <h1>Scene Analysis</h1>
          <p className="pricing-price">
            <span className="data">{PRICING.price}</span> once, no subscription
          </p>
        </div>

        <p className="pricing-blurb">{PRICING.blurb}</p>

        <ul className="pricing-points">
          <li><Icon name="light" size={15} /> <span>Measures the actual light in front of you, not a category from a list.</span></li>
          <li><Icon name="pan" size={15} /> <span>Reads real pan speed from the phone's orientation sensor.</span></li>
          <li><Icon name="nd" size={15} /> <span>Recommends the exact ND filter for the light you're actually in, not the one the page assumed.</span></li>
        </ul>

        {unlocked ? (
          <div className="pricing-unlocked" role="status">
            <Icon name="aperture" size={16} /> You've already unlocked Scene Analysis. Open any recipe on
            your phone to use it.
          </div>
        ) : linkReady ? (
          <a className="pricing-buy pill-action pill-action-primary" href={link}>
            Unlock for {PRICING.price} with Stripe
          </a>
        ) : (
          <div className="pricing-setup" role="note">
            <strong>Setup needed:</strong> add your Stripe Payment Link in
            <code> src/config/pricing.js</code> (the <code>paymentLink</code> field), and set the
            link's post-payment redirect to <code>{PRICING.successPath}</code>. Until then this button
            is inactive.
          </div>
        )}

        <p className="pricing-fineprint">
          Everything else on this site is free: every recipe, every reference page, and the builder.
          Scene Analysis is the one paid extra. Payment is handled entirely by Stripe; this site
          never sees your card details.
        </p>
      </div>

      <div data-prerender-ready="true" style={{ display: "none" }} />
      <Footer />
    </main>
  );
}
